import maplibregl, {
  type LngLatBoundsLike,
  type Map as MapLibreMap,
  type StyleSpecification,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

export type LatLng = [number, number]
export type LngLat = [number, number]

const attribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'

export function createMapStyle(isDark: boolean): StyleSpecification {
  const baseTiles = isDark
    ? ['https://basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}.png']
    : ['https://basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png']
  const labelTiles = isDark
    ? ['https://basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}.png']
    : ['https://basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}.png']

  return {
    version: 8,
    sources: {
      base: {
        type: 'raster',
        tiles: baseTiles,
        tileSize: 256,
        attribution,
      },
      labels: {
        type: 'raster',
        tiles: labelTiles,
        tileSize: 256,
        attribution,
      },
    },
    layers: [
      {
        id: 'base',
        type: 'raster',
        source: 'base',
      },
      {
        id: 'labels',
        type: 'raster',
        source: 'labels',
      },
    ],
  }
}

export function latLngToLngLat(point: LatLng): LngLat {
  return [point[1], point[0]]
}

export function createMap({
  container,
  center,
  zoom,
  isDark,
  scrollZoom = true,
}: {
  container: HTMLElement
  center: LatLng
  zoom: number
  isDark: boolean
  scrollZoom?: boolean
}) {
  const map = new maplibregl.Map({
    container,
    style: createMapStyle(isDark),
    center: latLngToLngLat(center),
    zoom,
    attributionControl: {},
    scrollZoom,
  })

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')
  return map
}

export function fitMapToPoints({
  map,
  points,
  fallbackCenter,
  fallbackZoom,
  maxZoom = 14,
  padding = 48,
}: {
  map: MapLibreMap
  points: LngLat[]
  fallbackCenter: LatLng
  fallbackZoom: number
  maxZoom?: number
  padding?: number
}) {
  if (points.length === 0) {
    map.jumpTo({ center: latLngToLngLat(fallbackCenter), zoom: fallbackZoom })
    return
  }

  if (points.length === 1) {
    map.easeTo({ center: points[0], zoom: Math.min(maxZoom, 12), duration: 250 })
    return
  }

  const bounds = new maplibregl.LngLatBounds(points[0], points[0])
  for (const point of points.slice(1)) {
    bounds.extend(point)
  }

  map.fitBounds(bounds as LngLatBoundsLike, { padding, maxZoom, duration: 250 })
}

export function invalidateMapSize(map: MapLibreMap) {
  map.resize()
}

export function syncMapWhenStyleIsReady(map: MapLibreMap, sync: () => void) {
  let disposed = false
  let attempts = 0
  let timeout: number | undefined

  const run = () => {
    if (disposed) return
    if (map.isStyleLoaded()) {
      sync()
      return
    }

    if (attempts < 80) {
      attempts += 1
      timeout = window.setTimeout(run, 50)
    }
  }

  const handleStyleReady = () => {
    attempts = 0
    run()
  }

  run()
  map.on('style.load', handleStyleReady)
  map.on('idle', handleStyleReady)

  return () => {
    disposed = true
    if (timeout) window.clearTimeout(timeout)
    map.off('style.load', handleStyleReady)
    map.off('idle', handleStyleReady)
  }
}

export function escapeMapHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}
